// domain/usecase/auth/LoginUseCase.kt
package com.evoapps.evoshift.domain.usecase.auth

import com.evoapps.evoshift.domain.model.User
import com.evoapps.evoshift.domain.provider.AppleSignInClient
import com.evoapps.evoshift.domain.provider.GoogleSignInClient
import com.evoapps.evoshift.domain.repository.AuthRepository

class LoginUseCase(private val authRepository: AuthRepository) {
    suspend operator fun invoke(email: String, pass: String): Result<User> {
        return authRepository.login(email, pass)
    }
}

class RegisterUserUseCase(private val authRepository: AuthRepository) {
    suspend operator fun invoke(email: String, pass: String, displayName: String): Result<User> {
        return authRepository.register(email, pass, displayName)
    }
}

/** Signs in or registers with Google; success with null when the person closed the account picker. */
class GoogleSignInUseCase(
    private val googleSignIn: GoogleSignInClient,
    private val authRepository: AuthRepository
) {
    suspend operator fun invoke(): Result<User?> {
        val credential = try {
            googleSignIn.signIn()
        } catch (e: Exception) {
            return Result.failure(e)
        } ?: return Result.success(null)
        return authRepository.signInWithGoogle(credential)
    }
}

/** The sign-in provider is not enabled in Firebase Auth yet (e.g. Apple before the owner's setup). */
class SignInMethodDisabledException : Exception("This sign-in method is not enabled.")

/**
 * This app's flow for the sign-in provider is not set up on the server (the Mac app's Google and
 * Apple sign-in before the owner configured their client ids, SH-97).
 */
class SignInNotAvailableHereException : Exception("This sign-in method is not set up for this app yet.")

/** An account with this e-mail exists with another sign-in method (Firebase keeps one per e-mail). */
class AccountExistsWithOtherSignInException : Exception("An account with this email already exists.")

/** Signs in or registers with Apple (SH-92); success with null when the person closed Apple's sheet. */
class AppleSignInUseCase(
    private val appleSignIn: AppleSignInClient,
    private val authRepository: AuthRepository
) {
    /** Whether the platform offers Sign in with Apple (the buttons are hidden otherwise). */
    val isAvailable: Boolean get() = appleSignIn.isAvailable

    suspend operator fun invoke(): Result<User?> {
        val signIn = try {
            appleSignIn.signIn()
        } catch (e: Exception) {
            return Result.failure(e)
        } ?: return Result.success(null)
        return authRepository.signInWithApple(signIn)
    }
}

class ResetPasswordUseCase(private val authRepository: AuthRepository) {
    suspend operator fun invoke(email: String): Result<Unit> {
        return authRepository.sendPasswordResetEmail(email)
    }
}


/** The account cannot be deleted while the person still owns workplaces: those go first. */
class OwnsWorkplacesException : Exception("Delete or hand over your workplaces first.")

class DeleteAccountUseCase(private val authRepository: AuthRepository) {
    suspend operator fun invoke(): Result<Unit> = authRepository.deleteAccount()
}

class SignOutUseCase(private val authRepository: AuthRepository) {
    suspend operator fun invoke() {
        return authRepository.signOut()
    }
}